import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import React from 'react';

import {RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import {InputChangeEvent, InputFocusEvent} from 'util/Types';

import isValidEmail from './ValidateEmail';

// Keep in sync with benchling.parties.create_users:UserColumnTypes.
export enum UserColumnTypes {
  NAME = 'NAME',
  EMAIL = 'EMAIL',
  HANDLE = 'HANDLE',
}

export interface UserRowInfo {
  [UserColumnTypes.NAME]: string;
  [UserColumnTypes.EMAIL]: string;
  [UserColumnTypes.HANDLE]: string;
  hasEmailError: boolean;
}

interface UserRowProps {
  userRowInfo: UserRowInfo;
  onRemove: () => void;
  onUpdate: (userRowInfo: Partial<UserRowInfo>) => void;
}

export const EMPTY_USER_ROW_INFO: UserRowInfo = {
  [UserColumnTypes.NAME]: '',
  [UserColumnTypes.EMAIL]: '',
  [UserColumnTypes.HANDLE]: '',
  hasEmailError: false,
};

export class UserRow extends React.Component<UserRowProps> {
  _handleChangeName = (e: InputChangeEvent): void => {
    this.props.onUpdate({[UserColumnTypes.NAME]: e.target.value});
  };

  _handleChangeEmail = (e: InputChangeEvent): void => {
    this.props.onUpdate({[UserColumnTypes.EMAIL]: e.target.value});
  };

  _validateEmail = (e: InputFocusEvent): void => {
    const currentEmail = this.props.userRowInfo.EMAIL;
    this.props.onUpdate({
      hasEmailError: !isValidEmail(currentEmail),
    });
  };

  _handleChangeHandle = (e: InputChangeEvent): void => {
    this.props.onUpdate({[UserColumnTypes.HANDLE]: e.target.value});
  };

  override render(): JSX.Element {
    return (
      <div className={css(styles.userRow)}>
        <div className={css(this.props.userRowInfo.hasEmailError && styles.bottomMarginError)}>
          <TextField
            className={css(styles.userRowField)}
            onChange={this._handleChangeName}
            value={this.props.userRowInfo[UserColumnTypes.NAME]}
            label="Name"
            labelStyle={FieldLabel.STYLES.HIDDEN}
            placeholder="Name"
            dataTestAttrs={{component: 'UsersForm', element: 'input', key: 'name'}}
          />
        </div>
        <div>
          <TextField
            className={css(styles.userRowField)}
            inputClassName={css(this.props.userRowInfo.hasEmailError && styles.emailErrorBorderColor)}
            onChange={this._handleChangeEmail}
            onBlur={this._validateEmail}
            value={this.props.userRowInfo[UserColumnTypes.EMAIL]}
            label="Email"
            labelStyle={FieldLabel.STYLES.HIDDEN}
            placeholder="Email"
            dataTestAttrs={{component: 'UsersForm', element: 'input', key: 'email'}}
          />
          {this.props.userRowInfo.hasEmailError && (
            <span className={css(styles.emailErrorFontColor)}>Invalid email</span>
          )}
        </div>
        <div className={css(this.props.userRowInfo.hasEmailError && styles.bottomMarginError)}>
          <TextField
            className={css(styles.userRowField)}
            onChange={this._handleChangeHandle}
            value={this.props.userRowInfo[UserColumnTypes.HANDLE]}
            label="Username"
            labelStyle={FieldLabel.STYLES.HIDDEN}
            placeholder="Username"
            dataTestAttrs={{component: 'UsersForm', element: 'input', key: 'username'}}
          />
        </div>
        <div
          className={classNames(
            css(styles.userRowRemoveIcon),
            css(this.props.userRowInfo.hasEmailError && styles.bottomMarginError)
          )}
        >
          <BenchlingIcon icon="close" className="u-faded u-pointer" onClick={this.props.onRemove} />
        </div>
      </div>
    );
  }
}

interface UsersFormProps {
  onUpdate: (userRowInfos: Array<UserRowInfo>) => void;
  userRowInfos: Array<UserRowInfo>;
}

export default class UsersForm extends React.Component<UsersFormProps> {
  _handleAddUser = (): void => {
    this.props.onUpdate([...this.props.userRowInfos, EMPTY_USER_ROW_INFO]);
  };

  _handleRemoveUser = (index: number): void => {
    this.props.onUpdate(reactUpdate(this.props.userRowInfos, {$splice: [[index, 1]]}));
  };

  _handleUpdateUserRow = (index: number, userRowInfoUpdate: Partial<UserRowInfo>): void => {
    this.props.onUpdate(reactUpdate(this.props.userRowInfos, {[index]: {$merge: userRowInfoUpdate}}));
  };

  override render(): JSX.Element {
    return (
      <div>
        {this.props.userRowInfos.map((userRowInfo, i) => (
          <UserRow
            key={i}
            userRowInfo={userRowInfo}
            onUpdate={(updatedUserRowInfo: UserRowInfo) => {
              this._handleUpdateUserRow(i, updatedUserRowInfo);
            }}
            onRemove={() => {
              this._handleRemoveUser(i);
            }}
          />
        ))}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  userRow: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: 12,
  },
  userRowField: {
    flex: 1,
    marginRight: 12,
  },
  userRowRemoveIcon: {
    flex: 'none',
  },
  bottomMarginError: {
    marginBottom: 16.5,
  },
  emailErrorFontColor: {
    color: RED,
  },
  emailErrorBorderColor: {
    borderColor: RED,
  },
});
